import { getUserManagerSettings } from "@expert/authentication";
import { getLogger } from "@expert/logging";
import { getFeature } from "@soluto-private/expert-workspace-feature-flagging";
import type { User } from "oidc-client-ts";
import type React from "react";
import { useEffect, useState } from "react";
import { AuthProvider, hasAuthParams, useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import { setIdentityAsTestUser } from "@/analytics";
import { GlobalError } from "@/common-ui";
import { WorkspaceLoader } from "@/common-ui/WorkspaceLoader";
import { env } from "@/config";
import { USER_FRIENDLY_NAME_QS_KEY } from "@/shared-utils/constants";
import { ErrorText } from "./elements";

const LOGIN_REQUIRED_ERROR_TYPE = "Login_required";

interface AuthenticationProviderProps {
    children?: React.ReactNode;
}

interface AuthHandlerProps {
    children?: React.ReactNode;
}

function useOnSigninCallback() {
    const navigate = useNavigate();
    return (user?: User) => void navigate(user?.url_state ?? globalThis.location.pathname, { replace: true });
}

export function AuthenticationProvider({ children }: AuthenticationProviderProps) {
    const onSigninCallback = useOnSigninCallback();
    return (
        <AuthProvider
            {...getUserManagerSettings({
                oidcSsoUrl: env.oidcSsoUrl,
                oidcSsoClientId: env.oidcSsoClientId,
                oidcSsoAdditionalScope: env.oidcSsoAdditionalScope,
                employeeId: new URLSearchParams(window.location.search).get(USER_FRIENDLY_NAME_QS_KEY) ?? undefined,
                isDev: import.meta.env.VITE_ENV_TYPE === "development",
            })}
            onSigninCallback={onSigninCallback}
        >
            <AuthHandler>{children}</AuthHandler>
        </AuthProvider>
    );
}

const logger = getLogger({ module: "AuthenticationProvider" });

function AuthHandler({ children }: AuthHandlerProps) {
    const auth = useAuth();
    const [checkingIfTestUser, setCheckingIfTestUser] = useState<boolean>(true);

    useEffect(() => {
        if (!hasAuthParams() && !auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator) {
            logger.debug({ auth }, "auth login redirect");
            void auth.signinRedirect({
                nonce: crypto.randomUUID(),
                url_state: `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`,
            });
        }

        auth.events.addAccessTokenExpiring((event) => {
            logger.info({ event }, "Token expiring soon...");
        });

        auth.events.addSilentRenewError((error) => {
            logger.info({ error }, "Silent renew error, redirecting to login");
            void auth.signinRedirect({
                nonce: crypto.randomUUID(),
            });
        });
    }, [auth]);

    useEffect(() => {
        if (auth.isLoading) {
            return;
        }

        const checkIfUserIsTestUser = async () => {
            const isTestUser = await getFeature<boolean>("EWP-isTestUser", {
                environment: env.mode,
                channel: env.channel,
                employeeId: auth.user?.profile.employeeID ?? auth.user?.profile.userPrincipalName ?? "",
            });

            logger.info(`(Worker): isTestUser: '${isTestUser}'`);
            if (isTestUser) {
                setIdentityAsTestUser();
            }

            setCheckingIfTestUser(false);
        };

        void checkIfUserIsTestUser();
    }, [auth.isLoading, auth.user]);

    switch (auth.activeNavigator) {
        case "signinSilent":
            return <WorkspaceLoader msg="Signing you in..." />;
        case "signoutRedirect":
            return <WorkspaceLoader msg="Signing you out..." />;
        case undefined:
        case "signinRedirect":
        case "signinPopup":
        case "signoutPopup":
        case "signoutSilent":
        case "signinResourceOwnerCredentials":
            break;
    }

    if (auth.error) {
        if ((auth.error.innerError as Error & { error: string }).error === LOGIN_REQUIRED_ERROR_TYPE) {
            return <WorkspaceLoader msg="Signing you in..." />;
        }

        return <GlobalError title="An error occurred while signing you in" />;
    }

    if (auth.isLoading) return <WorkspaceLoader msg="Signing you in..." />;
    if (checkingIfTestUser) return <WorkspaceLoader msg="Checking identity..." />;
    if (auth.isAuthenticated) return <>{children} </>;
    return <ErrorText>Not Authenticated</ErrorText>;
}
